import React, { useCallback, useRef, useState } from 'react';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box } from '@mui/material';

const MIN_LEFT_WIDTH = 200;
const MIN_RIGHT_WIDTH = 320;
const KEYBOARD_STEP = 32;
const ACTIVE_HANDLE_STYLES = {
  '& .resize-bar': { bgcolor: 'primary.main', width: '3px' },
  '& .resize-grip': { bgcolor: 'primary.main', color: 'primary.contrastText' },
};

interface Props {
  children: [React.ReactNode, React.ReactNode];
  defaultLeftWidth?: number;
  storageKey?: string;
}

function readStoredWidth(storageKey?: string, fallback?: number) {
  if (!storageKey) {
    return fallback;
  }

  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored ? Number(stored) : NaN;
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export default function ResizableSplitPane({ children, defaultLeftWidth = 280, storageKey }: Props) {
  const [left, right] = children;
  const shellRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useState(() => readStoredWidth(storageKey, defaultLeftWidth) ?? defaultLeftWidth);
  const [isDragging, setIsDragging] = useState(false);

  const clampWidth = useCallback((candidateWidth: number) => {
    const shellWidth = shellRef.current?.getBoundingClientRect().width ?? candidateWidth;
    const maxLeftWidth = Math.max(MIN_LEFT_WIDTH, shellWidth - MIN_RIGHT_WIDTH);
    return Math.min(Math.max(candidateWidth, MIN_LEFT_WIDTH), maxLeftWidth);
  }, []);

  const persistWidth = useCallback(
    (width: number) => {
      setLeftWidth(width);

      if (!storageKey) {
        return;
      }

      try {
        window.localStorage.setItem(storageKey, String(width));
      } catch {
        // localStorage may be unavailable (private browsing, quota) - width just won't persist
      }
    },
    [storageKey],
  );

  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    const shellRect = shellRef.current?.getBoundingClientRect();
    if (!shellRect) {
      return;
    }

    setIsDragging(true);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      persistWidth(clampWidth(moveEvent.clientX - shellRect.left));
    };
    const stopResize = () => {
      setIsDragging(false);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', stopResize);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', stopResize);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      persistWidth(clampWidth(leftWidth - KEYBOARD_STEP));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      persistWidth(clampWidth(leftWidth + KEYBOARD_STEP));
    }
  };

  return (
    <Box ref={shellRef} sx={{ display: 'flex', minHeight: 480, alignItems: 'stretch' }}>
      <Box sx={{ width: leftWidth, flexShrink: 0, overflowY: 'auto' }}>{left}</Box>
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panes"
        tabIndex={0}
        onPointerDown={startResize}
        onKeyDown={handleKeyDown}
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '12px',
          flexShrink: 0,
          cursor: 'col-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover, &:focus-visible': ACTIVE_HANDLE_STYLES,
          ...(isDragging && ACTIVE_HANDLE_STYLES),
        }}
      >
        <Box
          className="resize-bar"
          sx={{
            width: '2px',
            height: '100%',
            bgcolor: 'divider',
            transition: 'background-color 120ms ease, width 120ms ease',
          }}
        />
        <Box
          className="resize-grip"
          sx={{
            position: 'absolute',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 32,
            borderRadius: 1,
            bgcolor: 'action.disabledBackground',
            color: 'text.secondary',
            transition: 'background-color 120ms ease, color 120ms ease',
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16, transform: 'rotate(90deg)' }} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>{right}</Box>
    </Box>
  );
}
